import { Link2, Loader2, Search } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { trpc } from '@/lib/trpc'

interface PlatformUserLinkProps {
  clientId: string
  linkedUserId: string
  onLinked: () => void
}

export function PlatformUserLink({
  clientId,
  linkedUserId,
  onLinked,
}: PlatformUserLinkProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  const linkMutation = trpc.admin.clients.linkUser.useMutation({
    onSuccess: () => {
      toast.success('Linked user updated')
      setSearchQuery('')
      setSelectedUserId(null)
      onLinked()
    },
    onError: (error) => {
      toast.error(`Failed to link user: ${error.message}`)
    },
  })

  const usersQuery = trpc.admin.clients.searchUsers.useQuery(
    { query: searchQuery },
    { enabled: searchQuery.length >= 2 },
  )

  const handleLink = useCallback(() => {
    if (!selectedUserId) return
    linkMutation.mutate({ clientId, userId: selectedUserId })
  }, [clientId, selectedUserId, linkMutation])

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Link2 size={16} className="text-green-500" />
        <span className="text-foreground text-sm">
          Linked to user <code className="text-xs">{linkedUserId}</code>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={14}
            className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
          />
          <Input
            type="text"
            aria-label="Search platform users to re-link this client"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setSelectedUserId(null)
            }}
            placeholder="Search users to re-link this client..."
            className="pl-8"
          />
        </div>
        <Button
          size="sm"
          onClick={handleLink}
          disabled={!selectedUserId || linkMutation.isPending}
        >
          {linkMutation.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Link2 size={14} />
          )}
          Change
        </Button>
      </div>
      {usersQuery.data && usersQuery.data.length > 0 && (
        <ul className="border-border max-h-40 overflow-y-auto rounded-md border">
          {usersQuery.data.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                aria-pressed={selectedUserId === u.id}
                onClick={() => setSelectedUserId(u.id)}
                className={`hover:bg-muted w-full px-3 py-2 text-left text-sm ${
                  selectedUserId === u.id ? 'bg-muted' : ''
                }`}
              >
                <span className="text-foreground font-medium">
                  {u.firstName} {u.lastName}
                </span>
                <span className="text-muted-foreground ml-2 text-xs">
                  {u.email}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {usersQuery.data &&
        usersQuery.data.length === 0 &&
        searchQuery.length >= 2 && (
          <p className="text-muted-foreground text-xs">No users found</p>
        )}
    </div>
  )
}
